import {
  useLazyGetCountryCodesQuery,
  usePostPartnerMutation,
} from '@/api/api.slice.tsx';
import { PartnerType } from '@/api/types.ts';
import { useAppSelector } from '@/app/hooks.ts';
import { getOnBoardingState } from '@/features/OnBoarding/OnBoarding.slice.ts';
import {
  Alert,
  assertNotNull,
  AutocompleteLazy,
  Button,
  Country,
  getMessageFromError,
  InputPhone,
  InputPhoneChangeHandler,
  Paper,
  TextField,
  Typography,
  useAutocompleteLazyFetch,
} from '@arsnovasystems/ars-front-lib';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import FaceRoundedIcon from '@mui/icons-material/FaceRounded';
import { Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

interface FormData {
  companyName: string;
  partnerAddress: string;
  partnerTown: string;
  partnerZipcode: string;
  partnerCountryCode: Country | null;
  partnerState: string;
  firstName: string;
  lastName: string;
  phoneNumber: null | {
    value: string;
    isValid: boolean;
  };
}

/**
 * Step account form.
 *
 * @param props         props of the component
 * @param props.partner the partner object, coming from database
 *
 * @returns             JSX of the component
 */
export const StepAccountForm = ({ partner }: { partner: PartnerType }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [savePartner, savePartnerResult] = usePostPartnerMutation();
  const onBoarding = useAppSelector(getOnBoardingState);

  const [getCountryCodesLazy] = useLazyGetCountryCodesQuery();

  const { control, handleSubmit } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      companyName: partner.name,
      partnerAddress: partner.address,
      partnerTown: partner.town,
      partnerZipcode: partner.zipCode,
      partnerCountryCode: partner.country,
      partnerState: partner.stateRegion,
      firstName: partner.firstName,
      lastName: partner.lastName,
      phoneNumber:
        partner.phoneNumber ?
          {
            value: partner.phoneNumber,
            isValid: true,
          }
        : null,
    },
  });

  const fetchCountries = useAutocompleteLazyFetch<Country>({
    trigger: getCountryCodesLazy,
  });

  const onSubmit = async (data: FormData) => {
    assertNotNull(data.partnerCountryCode);
    assertNotNull(data.phoneNumber);

    const { error } = await savePartner({
      ...onBoarding,
      name: data.companyName,
      address: data.partnerAddress,
      country: data.partnerCountryCode,
      stateRegion: data.partnerState,
      town: data.partnerTown,
      zipCode: data.partnerZipcode,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber.value,
    });

    if (!error) {
      void navigate(`/onboarding/step/branding${location.search}`);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack
        sx={{
          width: '100%',
          gap: 2,
        }}
      >
        <Paper
          sx={(theme) => ({
            flex: 1,
            width: '100%',
            boxShadow: theme.shadowsCustom.softPaper,
          })}
        >
          <Stack
            direction={'row'}
            sx={{ alignItems: 'center' }}
          >
            <BusinessRoundedIcon
              sx={(theme) => ({ color: theme.palette.secondary.main })}
            />
            <Typography
              variant={'subtitle1'}
              color={'textSecondary'}
              sx={{ ml: 1 }}
            >
              {t('ik.company.information')}
            </Typography>
          </Stack>
          <Stack sx={{ gap: 2, marginTop: 3 }}>
            <Controller
              name={'companyName'}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    fullWidth={true}
                    error={fieldState.invalid}
                    label={t('ik.company.name')}
                  />
                );
              }}
            />
            <Controller
              name={'partnerAddress'}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    fullWidth={true}
                    error={fieldState.invalid}
                    label={t('ik.address')}
                  />
                );
              }}
            />
            <Stack
              direction={'row'}
              sx={{ gap: 1 }}
            >
              <Controller
                name={'partnerTown'}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <TextField
                      {...field}
                      fullWidth={true}
                      error={fieldState.invalid}
                      label={t('ik.town')}
                    />
                  );
                }}
              />
              <Controller
                name={'partnerZipcode'}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <TextField
                      {...field}
                      fullWidth={true}
                      error={fieldState.invalid}
                      label={t('ik.zip.code')}
                    />
                  );
                }}
              />
            </Stack>
            <Stack
              direction={'row'}
              sx={{ gap: 1 }}
            >
              <Controller
                name={'partnerState'}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <TextField
                      {...field}
                      fullWidth={true}
                      error={fieldState.invalid}
                      label={t('ik.onboarding.stepTwo.stateRegion')}
                    />
                  );
                }}
              />
              <Controller
                name={'partnerCountryCode'}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState }) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { ref, ...fieldRest } = field;

                  const handleChangeCountry = (
                    _e: React.SyntheticEvent,
                    value: Country | null,
                  ) => {
                    field.onChange(value);
                  };

                  return (
                    <AutocompleteLazy<Country>
                      {...fieldRest}
                      fetch={fetchCountries}
                      loadMore={fetchCountries}
                      fullWidth={true}
                      error={fieldState.invalid}
                      label={t('ik.country')}
                      getOptionLabel={(countryCode) => countryCode.countryName}
                      value={field.value}
                      onChange={handleChangeCountry}
                    />
                  );
                }}
              />
            </Stack>
          </Stack>
        </Paper>
        <Paper
          sx={(theme) => ({
            flex: 1,
            width: '100%',
            boxShadow: theme.shadowsCustom.softPaper,
          })}
        >
          <Stack sx={{ gap: 2 }}>
            <Stack
              direction={'row'}
              sx={{ alignItems: 'center' }}
            >
              <FaceRoundedIcon
                sx={(theme) => ({ color: theme.palette.secondary.main })}
              />
              <Typography
                variant={'subtitle1'}
                color={'textSecondary'}
                sx={{ ml: 1 }}
              >
                {t('ik.contact')}
              </Typography>
            </Stack>

            <Controller
              name={'lastName'}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    sx={{
                      marginTop: 2,
                    }}
                    fullWidth={true}
                    error={fieldState.invalid}
                    label={t('ik.last.name')}
                  />
                );
              }}
            />

            <Controller
              name={'firstName'}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    fullWidth={true}
                    error={fieldState.invalid}
                    label={t('ik.first.name')}
                  />
                );
              }}
            />

            <Controller
              name={'phoneNumber'}
              control={control}
              rules={{
                validate: {
                  checkValid: (field) => {
                    if (!field) return 'Invalid phone number';
                    if (!field.isValid) return 'Invalid phone number';
                  },
                },
              }}
              render={({ field, fieldState }) => {
                const handlePhoneChange: InputPhoneChangeHandler = ({
                  isValid,
                  phoneIndicator,
                  phoneNumber,
                }) => {
                  field.onChange({
                    isValid: isValid,
                    value: (phoneIndicator ?? '') + (phoneNumber ?? ''),
                  });
                };

                return (
                  <InputPhone
                    onChange={handlePhoneChange}
                    error={fieldState.invalid}
                    label={t('ik.phone.number')}
                    value={
                      partner.phoneNumber ?
                        '+' + partner.phoneNumber
                      : undefined
                    }
                  />
                );
              }}
            />
          </Stack>
        </Paper>

        {savePartnerResult.error && (
          <Alert>{getMessageFromError(savePartnerResult.error)}</Alert>
        )}

        <Stack
          direction={'row'}
          sx={{ gap: 1, justifyContent: 'center', mt: 5 }}
        >
          <Button
            variant={'outlined'}
            onClick={() => {
              void navigate(`/onboarding/step/welcome${location.search}`);
            }}
            size={'large'}
          >
            {t('Back')}
          </Button>
          <Button
            size={'large'}
            type={'submit'}
          >
            {t('Next')}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
